import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vModelCheckbox as _vModelCheckbox, unref as _unref, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = {
  class: "rounded-xl border border-gray-200 bg-white",
  "data-testid": "school-change"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "border-b border-gray-100 px-4 py-3" }, [
  /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold text-gray-900" }, "Change learners' school"),
  /*#__PURE__*/_createElementVNode("p", { class: "text-sm text-gray-500" }, " Move learners to another school, here or in another region. Their progress goes with them; custom lists from their old school end. ")
], -1)
const _hoisted_3 = { class: "grid gap-6 p-4 md:grid-cols-2" }
const _hoisted_4 = { class: "space-y-3" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs font-semibold uppercase tracking-wide text-gray-500" }, "From", -1)
const _hoisted_6 = ["onSubmit"]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("button", {
  type: "submit",
  class: "rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
}, "Find", -1)
const _hoisted_8 = {
  key: 0,
  class: "divide-y divide-gray-100 rounded-lg border border-gray-200"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_11 = {
  key: 2,
  class: "space-y-2"
}
const _hoisted_12 = { class: "flex items-center justify-between text-sm" }
const _hoisted_13 = { class: "font-semibold text-gray-900" }
const _hoisted_14 = {
  key: 0,
  class: "text-sm text-red-700",
  role: "alert"
}
const _hoisted_15 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_16 = {
  key: 2,
  class: "max-h-72 overflow-y-auto rounded-lg border border-gray-200"
}
const _hoisted_17 = { class: "flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500" }
const _hoisted_18 = ["checked"]
const _hoisted_19 = ["value", "aria-label"]
const _hoisted_20 = { class: "font-medium text-gray-900" }
const _hoisted_21 = { class: "block text-xs text-gray-500" }
const _hoisted_22 = { class: "space-y-3" }
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs font-semibold uppercase tracking-wide text-gray-500" }, "To", -1)
const _hoisted_24 = ["onSubmit"]
const _hoisted_25 = ["value"]
const _hoisted_26 = /*#__PURE__*/_createElementVNode("button", {
  type: "submit",
  class: "rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
}, "Find", -1)
const _hoisted_27 = {
  key: 0,
  class: "divide-y divide-gray-100 rounded-lg border border-gray-200"
}
const _hoisted_28 = ["onClick"]
const _hoisted_29 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_30 = {
  key: 2,
  class: "space-y-2"
}
const _hoisted_31 = { class: "flex items-center justify-between text-sm" }
const _hoisted_32 = { class: "font-semibold text-gray-900" }
const _hoisted_33 = { class: "font-normal text-gray-500" }
const _hoisted_34 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_35 = {
  key: 1,
  class: "max-h-56 overflow-y-auto rounded-lg border border-gray-200"
}
const _hoisted_36 = ["value", "aria-label"]
const _hoisted_37 = { class: "flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3" }
const _hoisted_38 = { class: "text-sm text-gray-600" }
const _hoisted_39 = ["disabled"]

import { computed, ref, watch } from "vue";
import { useRegionMoveStore, SchoolLearner, SchoolOption } from "@/stores/regionMoveStore";


export default /*#__PURE__*/_defineComponent({
  __name: 'SchoolChangeComponent',
  emits: ["open"],
  setup(__props, { emit }) {


const store = useRegionMoveStore();
const regions = computed(() => [...new Set([store.region, "eu", "au"].filter(Boolean))]);

const fromQuery = ref("");
const fromResults = ref<SchoolOption[]>([]);
const fromSearched = ref(false);
const fromSchool = ref<SchoolOption | null>(null);
const learners = ref<SchoolLearner[]>([]);
const loadingLearners = ref(false);
const learnersError = ref(false);
const selected = ref<string[]>([]);

// The parent learns the region after this mounts, so follow it until the
// operator picks one themselves; otherwise an AU dashboard would search EU.
const toRegion = ref(store.region || "eu");
const toRegionChosen = ref(false);
watch(
  () => store.region,
  region => {
    if (region && !toRegionChosen.value) toRegion.value = region;
  },
  { immediate: true }
);
const toQuery = ref("");
const toResults = ref<SchoolOption[]>([]);
const toSearched = ref(false);
const toSchool = ref<SchoolOption | null>(null);
const classIds = ref<string[]>([]);

async function search(region: string, q: string) {
  try {
    return await store.searchSchools(region, q.trim());
  } catch (error) {
    const data = (error as { response?: { data?: { error?: unknown } } })?.response?.data;
    store.error = typeof data?.error === "string" ? data.error : "The schools could not be searched.";
    return [];
  }
}

async function findFrom() {
  fromResults.value = await search(store.region, fromQuery.value);
  fromSearched.value = true;
}

async function chooseFrom(school: SchoolOption) {
  fromSchool.value = school;
  selected.value = [];
  loadingLearners.value = true;
  learnersError.value = false;
  learners.value = [];
  try {
    learners.value = await store.fetchSchoolLearners(school._id);
  } catch {
    // Never "no learners" when the list did not load: that would send the
    // operator looking for them in the wrong place.
    learnersError.value = true;
  } finally {
    loadingLearners.value = false;
  }
}

function resetFrom() {
  fromSchool.value = null;
  learnersError.value = false;
  learners.value = [];
  selected.value = [];
}

function toggleAll() {
  selected.value = selected.value.length === learners.value.length ? [] : learners.value.map(l => l._id);
}

async function findTo() {
  toResults.value = await search(toRegion.value, toQuery.value);
  toSearched.value = true;
}

function chooseTo(school: SchoolOption) {
  toSchool.value = school;
  classIds.value = [];
}

function resetTo() {
  toSchool.value = null;
  toResults.value = [];
  toSearched.value = false;
  classIds.value = [];
}

const sameSchool = computed(() => !!fromSchool.value && !!toSchool.value && fromSchool.value._id === toSchool.value._id && toRegion.value === store.region);
const ready = computed(() => selected.value.length > 0 && !!toSchool.value && !sameSchool.value);

const summary = computed(() => {
  if (sameSchool.value) return "Choose a different school to move them to.";
  if (!selected.value.length || !toSchool.value) return "Choose the learners and their new school.";
  const who = `${selected.value.length} learner${selected.value.length === 1 ? "" : "s"}`;
  const where = toRegion.value === store.region ? toSchool.value.name : `${toSchool.value.name} in ${toRegion.value.toUpperCase()}, moving region with them`;
  return `${who} to ${where}. Nothing changes until you approve it.`;
});

async function start() {
  if (!toSchool.value) return;
  const created = await store.changeSchool(selected.value, toRegion.value, toSchool.value, classIds.value);
  if (created) {
    resetFrom();
    resetTo();
    fromQuery.value = "";
    toQuery.value = "";
    emit("open", created._id);
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _hoisted_5,
        _createElementVNode("form", {
          class: "flex gap-2",
          onSubmit: _withModifiers(findFrom, ["prevent"])
        }, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fromQuery).value = $event)),
            type: "search",
            "aria-label": "Their current school",
            placeholder: "Their current school",
            class: "min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          }, null, 512), [
            [_vModelText, fromQuery.value]
          ]),
          _hoisted_7
        ], 40, _hoisted_6),
        (!fromSchool.value && fromResults.value.length)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fromResults.value, (school) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: school._id
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "w-full px-3 py-2 text-left text-sm hover:bg-gray-50",
                    onClick: ($event: any) => (chooseFrom(school))
                  }, _toDisplayString(school.name), 9, _hoisted_9)
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (fromSearched.value && !fromResults.value.length && !fromSchool.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_10, "No school here matches."))
          : _createCommentVNode("", true),
        (fromSchool.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("span", _hoisted_13, _toDisplayString(fromSchool.value.name), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-blue-600 hover:underline",
                  onClick: resetFrom
                }, "Change")
              ]),
              (learnersError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_14, [
                    _createTextVNode(" The learners could not be loaded. "),
                    _createElementVNode("button", {
                      type: "button",
                      class: "font-semibold underline",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (chooseFrom(fromSchool.value)))
                    }, "Try again")
                  ]))
                : (!learners.value.length)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(loadingLearners.value ? "Loading learners..." : "This school has no learners."), 1))
                  : (_openBlock(), _createElementBlock("div", _hoisted_16, [
                      _createElementVNode("label", _hoisted_17, [
                        _createElementVNode("input", {
                          type: "checkbox",
                          class: "h-4 w-4",
                          checked: selected.value.length === learners.value.length,
                          "aria-label": "Select every learner",
                          onChange: toggleAll
                        }, null, 40, _hoisted_18),
                        _createTextVNode(" " + _toDisplayString(selected.value.length) + " of " + _toDisplayString(learners.value.length) + " chosen ", 1)
                      ]),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(learners.value, (learner) => {
                        return (_openBlock(), _createElementBlock("label", {
                          key: learner._id,
                          class: "flex items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                        }, [
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selected).value = $event)),
                            type: "checkbox",
                            class: "mt-0.5 h-4 w-4",
                            value: learner._id,
                            "aria-label": `Choose ${learner.name}`
                          }, null, 8, _hoisted_19), [
                            [_vModelCheckbox, selected.value]
                          ]),
                          _createElementVNode("span", null, [
                            _createElementVNode("span", _hoisted_20, _toDisplayString(learner.name || learner._id), 1),
                            _createElementVNode("span", _hoisted_21, _toDisplayString([learner.email, learner.classes.join(", ")].filter(Boolean).join(" · ")), 1)
                          ])
                        ]))
                      }), 128))
                    ]))
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_22, [
        _hoisted_23,
        _createElementVNode("form", {
          class: "flex gap-2",
          onSubmit: _withModifiers(findTo, ["prevent"])
        }, [
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((toRegion).value = $event)),
            "aria-label": "Region",
            class: "rounded-lg border border-gray-300 py-2 pl-3 pr-8 text-sm",
            onChange: _cache[4] || (_cache[4] = ($event: any) => {toRegionChosen.value = true; resetTo()})
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(regions.value, (region) => {
              return (_openBlock(), _createElementBlock("option", {
                key: region,
                value: region
              }, _toDisplayString(region.toUpperCase()) + _toDisplayString(region === _unref(store).region ? " (here)" : ""), 9, _hoisted_25))
            }), 128))
          ], 544), [
            [_vModelSelect, toRegion.value]
          ]),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((toQuery).value = $event)),
            type: "search",
            "aria-label": "Their new school",
            placeholder: "Their new school",
            class: "min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          }, null, 512), [
            [_vModelText, toQuery.value]
          ]),
          _hoisted_26
        ], 40, _hoisted_24),
        (!toSchool.value && toResults.value.length)
          ? (_openBlock(), _createElementBlock("ul", _hoisted_27, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(toResults.value, (school) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: school._id
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "w-full px-3 py-2 text-left text-sm hover:bg-gray-50",
                    onClick: ($event: any) => (chooseTo(school))
                  }, _toDisplayString(school.name), 9, _hoisted_28)
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (toSearched.value && !toResults.value.length && !toSchool.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_29, "No school in " + _toDisplayString(toRegion.value.toUpperCase()) + " matches.", 1))
          : _createCommentVNode("", true),
        (toSchool.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
              _createElementVNode("div", _hoisted_31, [
                _createElementVNode("span", _hoisted_32, [
                  _createTextVNode(_toDisplayString(toSchool.value.name) + " ", 1),
                  _createElementVNode("span", _hoisted_33, "in " + _toDisplayString(toRegion.value.toUpperCase()), 1)
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "text-blue-600 hover:underline",
                  onClick: resetTo
                }, "Change")
              ]),
              (!toSchool.value.classes.length)
                ? (_openBlock(), _createElementBlock("p", _hoisted_34, "This school has no classes yet; the learners join it without one."))
                : (_openBlock(), _createElementBlock("div", _hoisted_35, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(toSchool.value.classes, (klass) => {
                      return (_openBlock(), _createElementBlock("label", {
                        key: klass._id,
                        class: "flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((classIds).value = $event)),
                          type: "checkbox",
                          class: "h-4 w-4",
                          value: klass._id,
                          "aria-label": `Put them in ${klass.name}`
                        }, null, 8, _hoisted_36), [
                          [_vModelCheckbox, classIds.value]
                        ]),
                        _createTextVNode(" " + _toDisplayString(klass.name), 1)
                      ]))
                    }), 128))
                  ]))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_37, [
      _createElementVNode("p", _hoisted_38, _toDisplayString(summary.value), 1),
      _createElementVNode("button", {
        type: "button",
        class: "rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300",
        disabled: !ready.value || _unref(store).acting,
        "data-testid": "start-school-change",
        onClick: start
      }, " Check the change ", 8, _hoisted_39)
    ])
  ]))
}
}

})